import { useState, useEffect, useMemo, useContext } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

import { setupAxiosInterceptors } from "./services/interceptor";
import ProtectedRoute from "core/ProtectedRoute";
import AdminProtectedRoute from "core/AdminProtectedRoute";
import ForgotPassword from "layouts/auth/forgot-password";
import ResetPassword from "layouts/auth/reset-password";
import ResetPasswordPage from "layouts/auth/reset-password-page";
import Login from "layouts/auth/login";
import Register from "layouts/auth/register";
import ActivationMessage from "layouts/auth/components/ActivationMessage";
import ActivateAccount from "layouts/auth/activate";
import { AuthContext } from "context";
import UserProfile from "layouts/user-profile";
import UserManagement from "layouts/user-management";
import Dashboard from "layouts/dashboard";
import BudgetDashboard from "layouts/revamped-budget/BudgetDashboard";
import TopNavbar from "core/Navbars/TopNavbar";

// Services
import JwtService from "services/jwt-service";

export default function App() {
  const authContext = useContext(AuthContext);

  const [controller, dispatch] = useMaterialUIController();
  const {
    direction,
    layout,
    mode,
  } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate();
  setupAxiosInterceptors(() => {
    authContext.logout();
    navigate("/login");
  });

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Get token from localStorage to determine default route
  const getDefaultRoute = () => {
    const token = localStorage.getItem("token");
    // Both admins and regular users default to financial hub
    return "/financial-hub";
  };

  return (
    <>
      {mode === "dark" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={themeRTL}>
            <CssBaseline />
            {layout === "dashboard" && (
              <>
                <TopNavbar />
              </>
            )}
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/activation-message" element={<ActivationMessage />} />
              <Route path="/activate/:userId/:activationId" element={<ActivateAccount />} />
              <Route path="/reset-password/:resetId" element={<ResetPasswordPage />} />
              <Route
                path="/financial-hub"
                element={
                  <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-profile"
                element={
                  <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <AdminProtectedRoute>
                    <UserManagement />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/budget-planner"
                element={
                  <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                    <BudgetDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
            </Routes>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <TopNavbar />
            </>
          )}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/activation-message" element={<ActivationMessage />} />
            <Route path="/activate/:userId/:activationId" element={<ActivateAccount />} />
            <Route path="/reset-password/:resetId" element={<ResetPasswordPage />} />
            <Route
              path="/financial-hub"
              element={
                <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-profile"
              element={
                <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <AdminProtectedRoute>
                  <UserManagement />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/budget-planner"
              element={
                <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                  <BudgetDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
          </Routes>
        </ThemeProvider>
      )}
    </>
  );
}